import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import ImageUpload from '../../common/ImageUpload';

const { FiX, FiBriefcase, FiGlobe, FiPhone, FiDollarSign, FiZap } = FiIcons;

const CompanyModal = ({ isOpen, onClose, company }) => {
  const { addCompany, updateCompany } = useData();
  const [formData, setFormData] = useState({
    name: '', websiteUrl: '', phone: '', address: '', city: '', state: '', zip: '',
    leadSource: '', defaultHourlyRate: 75, ccFees: 'absorb', paymentTerms: 'Due on Receipt', notes: '', logoUrl: ''
  });

  useEffect(() => {
    if (company) setFormData(company);
    else setFormData({
      name: '', websiteUrl: '', phone: '', address: '', city: '', state: '', zip: '',
      leadSource: '', defaultHourlyRate: 75, ccFees: 'absorb', paymentTerms: 'Due on Receipt', notes: '', logoUrl: ''
    });
  }, [company, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (company) await updateCompany(company.id, formData);
    else await addCompany(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden">
          <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter flex items-center gap-3">
              <SafeIcon icon={FiBriefcase} className="text-blue-600" /> {company ? 'Edit Company' : 'New Company'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="text-slate-400" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[75vh] overflow-y-auto custom-scrollbar">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <ImageUpload 
                value={formData.logoUrl} 
                onChange={(url) => setFormData({ ...formData, logoUrl: url })} 
                label="Company Logo"
                className="w-24 h-24"
              />
              <div className="flex-1 w-full space-y-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Company Name</label>
                  <input required className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Website URL</label>
                <input type="url" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.websiteUrl} onChange={e => setFormData({ ...formData, websiteUrl: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Phone</label>
                <input className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Address Info</label>
              <input placeholder="Street Address" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} />
              <div className="grid grid-cols-3 gap-3">
                <input placeholder="City" className="p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} />
                <input placeholder="State" className="p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.state} onChange={e => setFormData({ ...formData, state: e.target.value })} />
                <input placeholder="Zip" className="p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.zip} onChange={e => setFormData({ ...formData, zip: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Lead Source</label>
                <input className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.leadSource} onChange={e => setFormData({ ...formData, leadSource: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Hourly Rate</label>
                <input type="number" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.defaultHourlyRate} onChange={e => setFormData({ ...formData, defaultHourlyRate: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">CC Fees</label>
                <select className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold appearance-none" value={formData.ccFees} onChange={e => setFormData({ ...formData, ccFees: e.target.value })}>
                  <option value="absorb">Absorb Fees</option>
                  <option value="pass">Pass to Client</option>
                  <option value="split">Split 50/50</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Payment Terms</label>
                <select className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold appearance-none" value={formData.paymentTerms} onChange={e => setFormData({ ...formData, paymentTerms: e.target.value })}>
                  <option>Due on Receipt</option>
                  <option>Net 15</option>
                  <option>Net 30</option>
                </select>
              </div>
            </div>

            <button type="submit" className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all">
              {company ? 'Update Company' : 'Create Company'}
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CompanyModal;